import * as THREE from 'three'
import { createBlockMaterials, createDamageOverlayMaterial } from './block-assets.js'
import { BLOCK_SIZE, BOMBS, FLAG, WORLD } from './constants.js'
import { Environment } from './environment.js'

const tmpVector = new THREE.Vector3()
const CAMERA_EASE = {
    normal: 4.5,
    deliberate: 2.2,
    follow: 5.8
}
const DAY_START_HOUR = 6
const DAY_END_HOUR = 18
const SKY_STATE_REFRESH_MS = 30000
const BACKDROP_SPAN = 104
const BACKDROP_ART_PATH = '/art/backgrounds/'
const PROJECTILE_ART_PATH = '/art/projectiles/'
const FLAG_ART_PATH = '/art/flags/'
const PROJECTILE_ART_FILES = {
    basic: 'stone-chip.png',
    fire: 'wood-breaker.png',
    iron: 'glass-shatter.png',
    explosive: 'blast-bomb.png'
}
const FLAG_DAMAGE_STAGES = [100, 80, 60, 40, 20, 0]
const SKY_RENDER_ORDER = -80
const CELESTIAL_BACKDROP_Z = -8.32
const CELESTIAL_RENDER_ORDER = -70
const PROJECTILE_Z = 0.86
const AIM_PREVIEW_Z = 0.96
const TRAJECTORY_DOT_SPACING = 0.28
const TRAJECTORY_DOT_RADIUS = 0.052
const TRAJECTORY_DOT_MIN_COUNT = 16
const TRAJECTORY_DOT_MAX_COUNT = 88
const TRAJECTORY_FADE_START_RATIO = 0.52
const TRAJECTORY_FADE_END_RATIO = 0.7
const TRAJECTORY_DOT_ANIMATION_SPEED = 8.2
const BLOCK_DAMAGE_OVERLAY_Z = BLOCK_SIZE.depth / 2 + 0.014
const BLOCK_DESTROYED_HOLD_MS = 160
const BLOCK_DESTROYED_FADE_MS = 360
const DESTROYER_PULL_BACK_SPACE = 4.45
const MODE_ENVIRONMENT = {
    builder: {
        hour: 11.4,
        isDay: true,
        sunProgress: 0.58,
        nightProgress: 0
    },
    destroyer: {
        hour: 17.25,
        isDay: true,
        sunProgress: 0.22,
        nightProgress: 0
    }
}
const MODE_BACKDROP_LAYERS = {
    builder: [
        { src: 'builder-clouds-far.png', width: 58, height: 15.65, x: -1.4, y: 4.8, z: -7.1, parallaxX: 0.82, parallaxY: 0.54, speed: 0.015, opacity: 0.68, repeat: true, renderOrder: -20 },
        { src: 'builder-clouds-near.png', width: 52, height: 14.02, x: 1.2, y: 4.2, z: -6.45, parallaxX: 0.72, parallaxY: 0.44, speed: 0.026, opacity: 0.74, repeat: true, renderOrder: -18 },
        { src: 'builder-mountains.png', width: 58, height: 13.13, x: 0, y: -3.35, z: -5.65, parallaxX: 0.52, parallaxY: 0.16, opacity: 0.98 },
        { src: 'builder-tree-line.png', width: 54, height: 14.92, x: 0.6, y: -4.72, z: -4.45, parallaxX: 0.34, parallaxY: 0.1, opacity: 0.92, sway: 0.09 },
        { src: 'builder-foreground-grass.png', width: 41, height: 10.3, x: 1.2, y: -7.3, z: -1.08, parallaxX: 0.08, parallaxY: 0, opacity: 1, sway: 0.14 }
    ],
    destroyer: [
        { src: 'destroyer-clouds-far.png', width: 58, height: 15.65, x: -1.6, y: 4.7, z: -7.1, parallaxX: 0.82, parallaxY: 0.54, speed: 0.018, opacity: 0.7, repeat: true, renderOrder: -20 },
        { src: 'destroyer-clouds-near.png', width: 52, height: 14.02, x: 1.2, y: 4.15, z: -6.45, parallaxX: 0.72, parallaxY: 0.44, speed: 0.028, opacity: 0.76, repeat: true, renderOrder: -18 },
        { src: 'destroyer-mountains.png', width: 58, height: 13.92, x: 0, y: -3.16, z: -5.65, parallaxX: 0.52, parallaxY: 0.16, opacity: 0.98 },
        { src: 'destroyer-tree-line.png', width: 54, height: 14.92, x: 0.6, y: -4.76, z: -4.45, parallaxX: 0.34, parallaxY: 0.1, opacity: 0.9, sway: 0.09 },
        { src: 'destroyer-foreground-grass.png', width: 42, height: 11.2, x: -0.8, y: -7.12, z: -1.08, parallaxX: 0.08, parallaxY: 0, opacity: 1, sway: 0.14 }
    ]
}
const SKY_PALETTE_STOPS = [
    {
        hour: 0,
        skyTop: 0x06152f,
        skyBottom: 0x132f57,
        horizon: 0x23456d,
        hemisphereSky: 0x9ebcff,
        hemisphereGround: 0x16213a,
        ambientIntensity: 1.05,
        keyColor: 0xbdd0ff,
        keyIntensity: 0.55,
        cloudColor: 0x8ea7ca,
        cloudOpacity: 0.4,
        farHill: 0x1d4658,
        farHillOpacity: 0.58,
        midHill: 0x264f56,
        midHillOpacity: 0.68,
        nearHill: 0x2b574d,
        nearHillOpacity: 0.76,
        overlayOpacity: 0.32
    },
    {
        hour: 5.4,
        skyTop: 0x1d4273,
        skyBottom: 0xf0a270,
        horizon: 0xffcf87,
        hemisphereSky: 0xffd5a8,
        hemisphereGround: 0x314b58,
        ambientIntensity: 1.2,
        keyColor: 0xffc27a,
        keyIntensity: 0.72,
        cloudColor: 0xffdac4,
        cloudOpacity: 0.58,
        farHill: 0x3d756b,
        farHillOpacity: 0.62,
        midHill: 0x517c61,
        midHillOpacity: 0.72,
        nearHill: 0x59794f,
        nearHillOpacity: 0.8,
        overlayOpacity: 0.18
    },
    {
        hour: 8,
        skyTop: 0x81c9eb,
        skyBottom: 0xd7f0f1,
        horizon: 0xffe5ad,
        hemisphereSky: 0xffffff,
        hemisphereGround: 0x6f8f8b,
        ambientIntensity: 1.95,
        keyColor: 0xffefc0,
        keyIntensity: 1.32,
        cloudColor: 0xffffff,
        cloudOpacity: 0.86,
        farHill: 0x73bfb1,
        farHillOpacity: 0.72,
        midHill: 0x6bb089,
        midHillOpacity: 0.8,
        nearHill: 0x609a69,
        nearHillOpacity: 0.88,
        overlayOpacity: 0.02
    },
    {
        hour: 12,
        skyTop: 0x68c0ef,
        skyBottom: 0xc6eef7,
        horizon: 0xf3f6d8,
        hemisphereSky: 0xffffff,
        hemisphereGround: 0x56748a,
        ambientIntensity: 2.18,
        keyColor: 0xffffff,
        keyIntensity: 1.66,
        cloudColor: 0xffffff,
        cloudOpacity: 0.9,
        farHill: 0x6bb6ab,
        farHillOpacity: 0.72,
        midHill: 0x679f80,
        midHillOpacity: 0.8,
        nearHill: 0x5d9970,
        nearHillOpacity: 0.86,
        overlayOpacity: 0
    },
    {
        hour: 17.2,
        skyTop: 0x6fb6da,
        skyBottom: 0xffb879,
        horizon: 0xffd18a,
        hemisphereSky: 0xffe7be,
        hemisphereGround: 0x5f654f,
        ambientIntensity: 1.75,
        keyColor: 0xffb35f,
        keyIntensity: 1.05,
        cloudColor: 0xffdfc9,
        cloudOpacity: 0.78,
        farHill: 0x629783,
        farHillOpacity: 0.68,
        midHill: 0x66835f,
        midHillOpacity: 0.78,
        nearHill: 0x65764d,
        nearHillOpacity: 0.84,
        overlayOpacity: 0.08
    },
    {
        hour: 19,
        skyTop: 0x102955,
        skyBottom: 0x432c68,
        horizon: 0xb56b72,
        hemisphereSky: 0xaac3ff,
        hemisphereGround: 0x1c2840,
        ambientIntensity: 1.08,
        keyColor: 0xc3d4ff,
        keyIntensity: 0.58,
        cloudColor: 0xa7b5d4,
        cloudOpacity: 0.48,
        farHill: 0x26495c,
        farHillOpacity: 0.6,
        midHill: 0x314f56,
        midHillOpacity: 0.7,
        nearHill: 0x385746,
        nearHillOpacity: 0.78,
        overlayOpacity: 0.28
    },
    {
        hour: 24,
        skyTop: 0x06152f,
        skyBottom: 0x132f57,
        horizon: 0x23456d,
        hemisphereSky: 0x9ebcff,
        hemisphereGround: 0x16213a,
        ambientIntensity: 1.05,
        keyColor: 0xbdd0ff,
        keyIntensity: 0.55,
        cloudColor: 0x8ea7ca,
        cloudOpacity: 0.4,
        farHill: 0x1d4658,
        farHillOpacity: 0.58,
        midHill: 0x264f56,
        midHillOpacity: 0.68,
        nearHill: 0x2b574d,
        nearHillOpacity: 0.76,
        overlayOpacity: 0.32
    }
]
const PALETTE_COLOR_KEYS = [
    'skyTop',
    'skyBottom',
    'horizon',
    'hemisphereSky',
    'hemisphereGround',
    'keyColor',
    'cloudColor',
    'farHill',
    'midHill',
    'nearHill'
]
const PALETTE_NUMBER_KEYS = [
    'ambientIntensity',
    'keyIntensity',
    'cloudOpacity',
    'farHillOpacity',
    'midHillOpacity',
    'nearHillOpacity',
    'overlayOpacity'
]

export class GameScene {
    constructor(canvas) {
        this.canvas = canvas
        this.scene = new THREE.Scene()
        this.scene.background = new THREE.Color(0x9fd7ee)
        this.camera = new THREE.OrthographicCamera(-16, 16, 9, -9, 0.1, 100)
        this.camera.position.set(0, 0, 18)
        this.camera.lookAt(0, 0, 0)

        this.renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            alpha: false,
            preserveDrawingBuffer: true
        })
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this.renderer.outputColorSpace = THREE.SRGBColorSpace

        this.root = new THREE.Group()
        this.worldGroup = new THREE.Group()
        this.effectGroup = new THREE.Group()
        this.scene.add(this.root, this.worldGroup, this.effectGroup)

        this.meshById = new Map()
        this.builderGhost = null
        this.currentCamera = { x: 0, y: -0.2, zoom: 1 }
        this.targetCamera = { x: 0, y: -0.2, zoom: 1 }
        this.cameraEase = CAMERA_EASE.normal
        this.viewWidth = WORLD.width
        this.viewHeight = WORLD.height
        this.playerTimeZone = getPlayerTimeZone()
        this.textureLoader = new THREE.TextureLoader()
        this.projectileTextures = new Map()
        this.flagTextures = new Map()
        this.activeBackdropMode = 'builder'
        this.backdropModeGroups = {}
        this.parallaxLayers = []
        this.cloudLayers = []
        this.cloudMaterials = []
        this.skyElapsed = 0
        this.lastSkyUpdateAt = 0
        this.lastClockState = null
        this.environment = new Environment(this.root)

        this.createLighting()
        this.createBackdrop()
        this.createLauncher()
        this.createDestroyerRuler()
        this.resize()

        window.addEventListener('resize', () => this.resize())
        window.visualViewport?.addEventListener('resize', () => this.resize())

        this.resizeObserver = new ResizeObserver(() => this.resize())
        this.resizeObserver.observe(document.documentElement)
    }

    resize() {
        const { width, height } = getViewportSize()
        const aspect = width / height
        const desktopFraming = width >= 700
        const viewHeight = desktopFraming
            ? Math.max(WORLD.height, WORLD.width / aspect)
            : WORLD.height
        const viewWidth = viewHeight * aspect
        this.viewWidth = viewWidth
        this.viewHeight = viewHeight

        this.camera.left = -viewWidth / 2
        this.camera.right = viewWidth / 2
        this.camera.top = viewHeight / 2
        this.camera.bottom = -viewHeight / 2
        this.camera.updateProjectionMatrix()
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
        this.renderer.setSize(width, height, false)
        this.layoutBackdrop()
    }

    render(delta = 1 / 60) {
        const ease = Math.min(1, delta * this.cameraEase)
        this.currentCamera.x += (this.targetCamera.x - this.currentCamera.x) * ease
        this.currentCamera.y += (this.targetCamera.y - this.currentCamera.y) * ease
        this.currentCamera.zoom += (this.targetCamera.zoom - this.currentCamera.zoom) * ease
        this.environment.update(delta, this.getVisibleWorld(), {
            dusk: !this.lastClockState?.isDay || (this.lastClockState?.hour ?? 12) >= 16.5
        })
        this.camera.position.x = this.currentCamera.x + this.environment.shakeOffset.x
        this.camera.position.y = this.currentCamera.y + this.environment.shakeOffset.y
        this.camera.zoom = this.currentCamera.zoom
        this.camera.updateProjectionMatrix()
        this.updateBackdrop(delta)
        this.animateReadyBullet(delta)
        this.renderer.render(this.scene, this.camera)
    }

    // Brief camera shake for heavy impacts (0.05 light, 0.3 big blast).
    shake(intensity) {
        this.environment.shake(intensity)
    }

    getVisibleWorld() {
        const zoom = Math.max(0.01, this.currentCamera.zoom)
        return {
            x: this.currentCamera.x,
            y: this.currentCamera.y,
            width: this.viewWidth / zoom,
            height: this.viewHeight / zoom
        }
    }

    animateReadyBullet(delta) {
        const readyBullet = this.meshById.get('ready-bullet')
        if (!readyBullet || readyBullet.userData.type !== 'bullet') {
            return
        }

        readyBullet.rotation.z += delta * 1.3
    }

    renderStill({ snapCamera = false } = {}) {
        if (snapCamera) {
            this.currentCamera = { ...this.targetCamera }
        }

        this.camera.position.x = this.currentCamera.x
        this.camera.position.y = this.currentCamera.y
        this.camera.zoom = this.currentCamera.zoom
        this.camera.updateProjectionMatrix()
        this.updateBackdrop(0)
        this.renderer.render(this.scene, this.camera)
    }

    captureFrame({ width = 1200, height = 630, snapCamera = false } = {}) {
        this.renderStill({ snapCamera })

        const output = document.createElement('canvas')
        output.width = width
        output.height = height

        const context = output.getContext('2d')
        drawImageCover(context, this.renderer.domElement, width, height)

        return output
    }

    captureBuildPreview({ width = 640, height = 640 } = {}) {
        return this.captureWorldBounds({
            width,
            height,
            bounds: {
                minX: WORLD.buildMinX - 0.32,
                maxX: WORLD.buildMaxX + 0.32,
                minY: WORLD.buildMinY - 0.18,
                maxY: WORLD.buildMaxY + 0.32
            },
            padding: 0.04,
            minZoom: 0.1,
            maxZoom: 4
        })
    }

    captureWorldBounds({ width, height, bounds, padding = 0.1, minZoom = 0.1, maxZoom = 4 }) {
        const output = document.createElement('canvas')
        output.width = width
        output.height = height

        const previous = {
            currentCamera: { ...this.currentCamera },
            targetCamera: { ...this.targetCamera },
            cameraEase: this.cameraEase,
            viewWidth: this.viewWidth,
            viewHeight: this.viewHeight
        }
        const viewport = getViewportSize()

        try {
            const captureAspect = width / height
            const captureViewHeight = WORLD.height
            const captureViewWidth = captureViewHeight * captureAspect
            this.viewWidth = captureViewWidth
            this.viewHeight = captureViewHeight
            this.camera.left = -captureViewWidth / 2
            this.camera.right = captureViewWidth / 2
            this.camera.top = captureViewHeight / 2
            this.camera.bottom = -captureViewHeight / 2
            this.camera.updateProjectionMatrix()
            this.renderer.setSize(width, height, false)
            this.layoutBackdrop()

            this.setCameraNow(this.getCameraTargetForBounds(bounds, { padding, minZoom, maxZoom }))
            this.renderStill({ snapCamera: true })
            output.getContext('2d').drawImage(this.renderer.domElement, 0, 0, width, height)
        } finally {
            this.currentCamera = previous.currentCamera
            this.targetCamera = previous.targetCamera
            this.cameraEase = previous.cameraEase
            this.viewWidth = previous.viewWidth
            this.viewHeight = previous.viewHeight
            this.camera.left = -this.viewWidth / 2
            this.camera.right = this.viewWidth / 2
            this.camera.top = this.viewHeight / 2
            this.camera.bottom = -this.viewHeight / 2
            this.camera.updateProjectionMatrix()
            this.renderer.setSize(viewport.width, viewport.height, false)
            this.layoutBackdrop()
            this.renderStill({ snapCamera: true })
        }

        return output
    }

    setCameraTarget(target, options = {}) {
        this.targetCamera = {
            x: target.x ?? this.targetCamera.x,
            y: target.y ?? this.targetCamera.y,
            zoom: target.zoom ?? this.targetCamera.zoom
        }

        if (options.ease) {
            this.cameraEase = options.ease
        }
    }

    setCameraNow(target) {
        this.currentCamera = {
            x: target.x ?? this.currentCamera.x,
            y: target.y ?? this.currentCamera.y,
            zoom: target.zoom ?? this.currentCamera.zoom
        }
        this.setCameraTarget(this.currentCamera)
    }

    panCameraByPixels(deltaX, deltaY, bounds = null) {
        const width = Math.max(1, this.canvas.clientWidth || this.canvas.width || window.innerWidth)
        const height = Math.max(1, this.canvas.clientHeight || this.canvas.height || window.innerHeight)
        const zoom = this.targetCamera.zoom || 1
        const next = {
            x: this.targetCamera.x - deltaX * (this.viewWidth / zoom / width),
            y: this.targetCamera.y + deltaY * (this.viewHeight / zoom / height),
            zoom
        }
        this.setCameraNow(bounds ? constrainCameraToBounds(next, bounds, this.viewWidth, this.viewHeight) : next)
    }

    zoomCameraAt(clientX, clientY, factor, bounds = null, options = {}) {
        if (!Number.isFinite(factor) || factor <= 0) {
            return
        }

        const rect = this.canvas.getBoundingClientRect()
        const width = Math.max(1, rect.width || this.canvas.clientWidth || window.innerWidth)
        const height = Math.max(1, rect.height || this.canvas.clientHeight || window.innerHeight)
        const ndcX = ((clientX - rect.left) / width) * 2 - 1
        const ndcY = -(((clientY - rect.top) / height) * 2 - 1)
        const currentZoom = this.targetCamera.zoom || 1
        const nextZoom = clamp(
            currentZoom * factor,
            options.minZoom ?? 0.75,
            options.maxZoom ?? 3.5
        )

        if (Math.abs(nextZoom - currentZoom) < 0.0001) {
            return
        }

        const anchor = {
            x: this.targetCamera.x + ndcX * (this.viewWidth / currentZoom / 2),
            y: this.targetCamera.y + ndcY * (this.viewHeight / currentZoom / 2)
        }
        const next = {
            x: anchor.x - ndcX * (this.viewWidth / nextZoom / 2),
            y: anchor.y - ndcY * (this.viewHeight / nextZoom / 2),
            zoom: nextZoom
        }

        this.setCameraNow(bounds ? constrainCameraToBounds(next, bounds, this.viewWidth, this.viewHeight) : next)
    }

    getCameraTargetForBounds(bounds, options = {}) {
        const padding = options.padding ?? 0.8
        const minX = bounds.minX - padding
        const maxX = bounds.maxX + padding
        const minY = bounds.minY - padding
        const maxY = bounds.maxY + padding
        const spanX = Math.max(1, maxX - minX)
        const spanY = Math.max(1, maxY - minY)
        const minZoom = options.minZoom ?? 0.75
        const maxZoom = options.maxZoom ?? 2.35
        const zoom = clamp(
            Math.min(this.viewWidth / spanX, this.viewHeight / spanY),
            minZoom,
            maxZoom
        )

        return {
            x: (minX + maxX) / 2,
            y: (minY + maxY) / 2,
            zoom
        }
    }

    frameBounds(bounds, options = {}) {
        const target = this.getCameraTargetForBounds(bounds, options)
        this.setCameraTarget(target, { ease: options.ease ?? CAMERA_EASE.normal })
        return target
    }

    frameStageOverview(options = {}) {
        return this.frameBounds({
            minX: WORLD.launcherX - DESTROYER_PULL_BACK_SPACE,
            maxX: WORLD.buildMaxX + 1.6,
            minY: WORLD.groundY - 0.85,
            maxY: 2.2
        }, {
            padding: 0.6,
            minZoom: 0.78,
            maxZoom: 1.18,
            ease: CAMERA_EASE.normal,
            ...options
        })
    }

    frameBuildArea(options = {}) {
        return this.frameBounds({
            minX: WORLD.buildMinX - 0.8,
            maxX: WORLD.buildMaxX + 0.8,
            minY: WORLD.groundY - 0.9,
            maxY: 2.9
        }, {
            padding: 0.45,
            minZoom: 1.05,
            maxZoom: 2.25,
            ease: CAMERA_EASE.deliberate,
            ...options
        })
    }

    resetCamera() {
        this.setStageVisible(false)
        this.setDestroyerRulerVisible(false)
        this.setCameraTarget({ x: 0, y: -0.15, zoom: 1 }, { ease: CAMERA_EASE.normal })
    }

    screenToWorld(clientX, clientY) {
        const rect = this.canvas.getBoundingClientRect()
        tmpVector.x = ((clientX - rect.left) / rect.width) * 2 - 1
        tmpVector.y = -(((clientY - rect.top) / rect.height) * 2 - 1)
        tmpVector.z = 0
        tmpVector.unproject(this.camera)
        return { x: tmpVector.x, y: tmpVector.y }
    }

    clearWorld() {
        for (const mesh of this.meshById.values()) {
            cancelMeshLifecycle(mesh)
            this.worldGroup.remove(mesh)
            disposeObject(mesh)
        }
        this.meshById.clear()
        this.clearBuilderGhost()
        this.clearEffects()
    }

    // Semi-transparent placement preview that follows the pointer in the
    // builder. The mesh is only rebuilt when its look changes.
    setBuilderGhost(block, valid = true) {
        if (!block) {
            this.clearBuilderGhost()
            return
        }

        const key = [block.material, block.shape, block.width, block.height, block.rotation || 0, valid].join('|')

        if (!this.builderGhost || this.builderGhost.key !== key) {
            this.clearBuilderGhost()
            const mesh = this.createBlockMesh({ ...block, id: 'builder-ghost' }, false, { preview: true })

            if (!valid) {
                mesh.traverse((child) => {
                    for (const material of getObjectMaterials(child)) {
                        material.color?.set(0xff4a3a)
                    }
                })
            }

            this.worldGroup.add(mesh)
            this.builderGhost = { key, mesh }
        }

        this.builderGhost.mesh.position.set(block.x, block.y, 0.02)
    }

    clearBuilderGhost() {
        if (!this.builderGhost) {
            return
        }

        this.worldGroup.remove(this.builderGhost.mesh)
        disposeObject(this.builderGhost.mesh)
        this.builderGhost = null
    }

    clearEffects() {
        while (this.effectGroup.children.length) {
            const child = this.effectGroup.children.pop()
            cancelMeshLifecycle(child)
            disposeObject(child)
        }
    }

    showBuilder(blocks, selectedId = null, flag = { x: WORLD.flagX, y: WORLD.flagY }, flagSelected = false, options = {}) {
        this.clearWorld()
        this.setStageMode('builder')
        this.setBuildZoneVisible(true)
        this.setLauncherVisible(false)
        this.setDestroyerRulerVisible(false)
        this.addFlagMesh('builder-flag', flag.x, flag.y, { selected: flagSelected })

        for (const block of blocks) {
            const mesh = this.createBlockMesh(block, block.id === selectedId, { preview: block.preview })
            this.worldGroup.add(mesh)
            this.meshById.set(block.id, mesh)
        }

        if (options.frameCamera !== false) {
            this.frameBuildArea({ minZoom: 1.2, maxZoom: 2.35 })
        }
    }

    setupDestroyScene() {
        this.clearWorld()
        this.setStageMode('destroyer')
        this.setBuildZoneVisible(false)
        this.setLauncherVisible(true)
        this.setDestroyerRulerVisible(false)
        this.clearEffects()
        this.setCameraNow(this.frameStageOverview({ ease: CAMERA_EASE.normal }))
    }

    createBlockMesh(block, selected = false, options = {}) {
        const width = block.width || BLOCK_SIZE.width
        const height = block.height || BLOCK_SIZE.height
        const preview = Boolean(options.preview)
        const materials = createBlockMaterials(block.material, width, height)

        if (preview) {
            for (const material of materials) {
                material.transparent = true
                material.opacity = Math.min(material.opacity ?? 1, 0.48)
                material.depthWrite = false
            }
        }

        const mesh = new THREE.Mesh(
            new THREE.BoxGeometry(width, height, BLOCK_SIZE.depth),
            materials
        )
        mesh.position.set(block.x, block.y, 0)
        mesh.rotation.z = block.rotation || 0
        mesh.userData = {
            id: block.id,
            type: 'block',
            preview,
            materialKey: block.material,
            width,
            height,
            damageState: 'intact',
            damageOverlay: null,
            destroying: false,
            destroyTimer: null,
            fadeFrame: null
        }

        const edge = new THREE.LineSegments(
            new THREE.EdgesGeometry(mesh.geometry),
            new THREE.LineBasicMaterial({
                color: preview ? 0xfff2b3 : selected ? 0xffffff : 0x1a1d25,
                transparent: true,
                opacity: preview ? 0.92 : selected ? 0.95 : 0.25
            })
        )
        edge.renderOrder = 2
        mesh.add(edge)

        if (selected || preview) {
            const halo = new THREE.Mesh(
                new THREE.BoxGeometry(width + 0.14, height + 0.14, 0.03),
                new THREE.MeshBasicMaterial({ color: preview ? 0xffe58a : 0xffffff, transparent: true, opacity: preview ? 0.2 : 0.16 })
            )
            halo.position.z = -0.03
            mesh.add(halo)
        }

        const contactShade = new THREE.Mesh(
            new THREE.PlaneGeometry(width * 0.96, 0.12),
            new THREE.MeshBasicMaterial({
                color: 0x17202a,
                transparent: true,
                opacity: preview ? 0.08 : 0.16,
                depthWrite: false
            })
        )
        contactShade.position.set(0, -height / 2 - 0.045, -0.24)
        mesh.add(contactShade)

        return mesh
    }

    setBlockDamage(id, healthRatio, options = {}) {
        const mesh = this.meshById.get(id)
        if (!mesh || mesh.userData.type !== 'block' || mesh.userData.preview || mesh.userData.destroying) {
            return
        }

        const materialKey = options.material || mesh.userData.materialKey || 'wood'
        const state = getBlockDamageState(materialKey, healthRatio)
        this.applyBlockDamageState(mesh, state, materialKey)
    }

    destroyBlockMesh(id, options = {}) {
        const mesh = this.meshById.get(id)
        if (!mesh || mesh.userData.type !== 'block') {
            this.removeMesh(id)
            return
        }

        if (mesh.userData.destroying) {
            return
        }

        mesh.userData.destroying = true
        const material = options.material || mesh.userData.materialKey || 'wood'
        this.addBlockBreakBurst(mesh.position.x, mesh.position.y, {
            material,
            width: mesh.userData.width,
            height: mesh.userData.height,
            angle: mesh.rotation.z || 0
        })
        this.applyBlockDamageState(mesh, 'destroyed', material)
        setObjectOpacity(mesh, 1)

        mesh.userData.destroyTimer = setTimeout(() => {
            mesh.userData.destroyTimer = null
            fadeObjectOut(mesh, BLOCK_DESTROYED_FADE_MS, () => this.removeMesh(id))
        }, BLOCK_DESTROYED_HOLD_MS)
    }

    applyBlockDamageState(mesh, state, materialKey) {
        if (mesh.userData.damageState === state) {
            return
        }

        removeDamageOverlay(mesh)
        mesh.userData.damageState = state

        if (state === 'intact') {
            return
        }

        const overlay = new THREE.Mesh(
            new THREE.PlaneGeometry(mesh.userData.width * 1.015, mesh.userData.height * 1.015),
            createDamageOverlayMaterial(materialKey, mesh.userData.width, mesh.userData.height, state)
        )
        overlay.position.z = BLOCK_DAMAGE_OVERLAY_Z
        overlay.renderOrder = state === 'destroyed' ? 6 : 5
        overlay.userData = { type: 'damage-overlay' }
        mesh.add(overlay)
        mesh.userData.damageOverlay = overlay
    }

    addBlockBodyMesh(block) {
        const mesh = this.createBlockMesh(block)
        this.worldGroup.add(mesh)
        this.meshById.set(block.id, mesh)
        return mesh
    }

    addBulletMesh(id, type, x, y, options = {}) {
        const config = BOMBS[type]
        const group = new THREE.Group()
        const radius = options.radius ?? config.radius
        const size = radius * (type === 'explosive' ? 2.9 : 2.72)
        const bullet = new THREE.Mesh(
            new THREE.PlaneGeometry(size, size),
            new THREE.MeshBasicMaterial({
                map: this.getProjectileTexture(type),
                color: 0xffffff,
                transparent: true,
                alphaTest: 0.05,
                depthWrite: false,
                side: THREE.DoubleSide
            })
        )
        bullet.renderOrder = 16
        group.add(bullet)
        group.position.set(x, y, PROJECTILE_Z)
        group.userData = { id, type: 'bullet', bulletType: type, bombType: type }
        this.worldGroup.add(group)
        this.meshById.set(id, group)
        return group
    }

    getProjectileTexture(type) {
        const key = PROJECTILE_ART_FILES[type] ? type : 'basic'
        if (this.projectileTextures.has(key)) {
            return this.projectileTextures.get(key)
        }

        const texture = this.textureLoader.load(`${PROJECTILE_ART_PATH}${PROJECTILE_ART_FILES[key]}`)
        texture.colorSpace = THREE.SRGBColorSpace
        texture.minFilter = THREE.LinearFilter
        texture.magFilter = THREE.LinearFilter
        texture.generateMipmaps = false
        this.projectileTextures.set(key, texture)
        return texture
    }

    getFlagTexture(stage) {
        const key = FLAG_DAMAGE_STAGES.includes(stage) ? stage : 100
        if (this.flagTextures.has(key)) {
            return this.flagTextures.get(key)
        }

        const texture = this.textureLoader.load(`${FLAG_ART_PATH}flag-${key}.png`)
        texture.colorSpace = THREE.SRGBColorSpace
        texture.minFilter = THREE.LinearFilter
        texture.magFilter = THREE.LinearFilter
        texture.generateMipmaps = false
        this.flagTextures.set(key, texture)
        return texture
    }

    addBombMesh(id, type, x, y) {
        return this.addBulletMesh(id, type, x, y)
    }

    addFlagMesh(id, x = WORLD.flagX, y = WORLD.flagY, options = {}) {
        const group = new THREE.Group()
        const flagSprite = new THREE.Mesh(
            new THREE.PlaneGeometry(FLAG.width * 1.42, FLAG.height * 1.42),
            new THREE.MeshBasicMaterial({
                map: this.getFlagTexture(100),
                transparent: true,
                alphaTest: 0.05,
                depthWrite: false,
                side: THREE.DoubleSide
            })
        )
        flagSprite.position.set(0.03, 0.02, 0.12)
        flagSprite.renderOrder = 12
        flagSprite.userData = { type: 'flag-sprite' }
        group.add(flagSprite)

        if (options.selected) {
            const halo = new THREE.Mesh(
                new THREE.RingGeometry(0.62, 0.72, 36),
                new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.54, depthTest: false, side: THREE.DoubleSide })
            )
            halo.position.set(0, 0, -0.03)
            halo.renderOrder = 12
            group.add(halo)
        }

        group.position.set(x, y, 0.22)
        group.userData = { id, type: 'flag', flagSprite, flagDamageStage: 100 }
        this.worldGroup.add(group)
        this.meshById.set(id, group)
        return group
    }

    setFlagDamage(id, healthRatio) {
        const group = this.meshById.get(id)
        if (!group || group.userData.type !== 'flag') {
            return
        }

        const ratio = clamp(Number.isFinite(healthRatio) ? healthRatio : 1, 0, 1)
        const stage = getFlagDamageStage(ratio)

        if (group.userData.flagDamageStage === stage) {
            return
        }

        removeFlagDamageOverlay(group)
        group.userData.flagDamageStage = stage

        const flagSprite = group.userData.flagSprite
        if (flagSprite?.material) {
            flagSprite.material.map = this.getFlagTexture(stage)
            flagSprite.material.needsUpdate = true
        }
    }

    addTrajectoryLine(points, type = 'basic') {
        this.clearEffects()

        if (points.length < 2) {
            return
        }

        const group = new THREE.Group()
        const config = BOMBS[type] || BOMBS.basic
        const color = new THREE.Color(config.accent || 0xffffff)
        const curvePoints = points.map((point) => new THREE.Vector3(point.x, point.y, AIM_PREVIEW_Z))
        const curve = curvePoints.length > 2
            ? new THREE.CatmullRomCurve3(curvePoints, false, 'centripetal', 0.35)
            : null
        const pathLength = curve
            ? curve.getLength()
            : curvePoints[0].distanceTo(curvePoints[curvePoints.length - 1])
        const dotCount = Math.min(
            TRAJECTORY_DOT_MAX_COUNT,
            Math.max(TRAJECTORY_DOT_MIN_COUNT, Math.ceil(pathLength / TRAJECTORY_DOT_SPACING))
        )
        const sampledPoints = curve
            ? curve.getSpacedPoints(Math.max(1, dotCount - 1))
            : sampleLinearPoints(curvePoints[0], curvePoints[curvePoints.length - 1], dotCount)
        const previewEndProgress = points[points.length - 1]?.trajectoryProgress ?? 1
        const animatedDots = []

        sampledPoints.forEach((point, index) => {
            const pathProgress = dotCount > 1 ? index / (dotCount - 1) : 0
            const trajectoryProgress = pathProgress * previewEndProgress
            const fade = getTrajectoryFade(trajectoryProgress)

            if (fade <= 0.025) {
                return
            }

            const shadow = new THREE.Mesh(
                new THREE.CircleGeometry(TRAJECTORY_DOT_RADIUS * 1.72, 14),
                new THREE.MeshBasicMaterial({
                    color: 0x111820,
                    transparent: true,
                    opacity: 0.22 * fade,
                    depthTest: false,
                    depthWrite: false,
                    side: THREE.DoubleSide
                })
            )
            const dot = new THREE.Mesh(
                new THREE.CircleGeometry(TRAJECTORY_DOT_RADIUS, 14),
                new THREE.MeshBasicMaterial({
                    color,
                    transparent: true,
                    opacity: 0.86 * fade,
                    depthTest: false,
                    depthWrite: false,
                    side: THREE.DoubleSide
                })
            )

            shadow.position.copy(point)
            dot.position.copy(point)
            shadow.renderOrder = 20
            dot.renderOrder = 21
            shadow.userData.baseOpacity = shadow.material.opacity
            dot.userData.baseOpacity = dot.material.opacity
            dot.userData.pathProgress = pathProgress
            animatedDots.push(dot)
            group.add(shadow, dot)
        })

        const startedAt = performance.now()
        const animate = (now) => {
            const elapsed = (now - startedAt) / 1000

            for (const dot of animatedDots) {
                const wave = (Math.sin((dot.userData.pathProgress * Math.PI * 7.2) - elapsed * TRAJECTORY_DOT_ANIMATION_SPEED) + 1) / 2
                const pulse = 0.72 + wave * 0.28
                dot.material.opacity = dot.userData.baseOpacity * pulse
                dot.scale.setScalar(0.86 + wave * 0.22)
            }

            if (group.parent) {
                group.userData.frame = requestAnimationFrame(animate)
            }
        }

        this.effectGroup.add(group)
        group.userData.frame = requestAnimationFrame(animate)
    }

    addBlast(x, y, radius, color = 0xffffff) {
        const group = new THREE.Group()
        const ring = new THREE.Mesh(
            new THREE.RingGeometry(radius * 0.34, radius * 0.52, 56),
            new THREE.MeshBasicMaterial({ color: 0xffd166, transparent: true, opacity: 0.72, side: THREE.DoubleSide, depthWrite: false })
        )
        const shockwave = new THREE.Mesh(
            new THREE.RingGeometry(radius * 0.62, radius, 64),
            new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.36, side: THREE.DoubleSide, depthWrite: false })
        )
        const flash = new THREE.Mesh(
            new THREE.CircleGeometry(radius * 0.42, 36),
            new THREE.MeshBasicMaterial({ color: 0xfff0b8, transparent: true, opacity: 0.62, side: THREE.DoubleSide, depthWrite: false })
        )
        const startedAt = performance.now()
        const duration = 560
        const puffCount = 12

        ring.position.z = 0.58
        shockwave.position.z = 0.56
        flash.position.z = 0.54
        group.position.set(x, y, 0)
        group.renderOrder = 32
        group.add(flash, shockwave, ring)

        for (let index = 0; index < puffCount; index += 1) {
            const angle = (Math.PI * 2 * index) / puffCount + Math.random() * 0.36
            const puff = new THREE.Mesh(
                new THREE.CircleGeometry(0.12 + Math.random() * 0.14, 16),
                new THREE.MeshBasicMaterial({
                    color: Math.random() > 0.5 ? 0xf1b45e : 0x4a4c50,
                    transparent: true,
                    opacity: 0.54,
                    side: THREE.DoubleSide,
                    depthWrite: false
                })
            )
            puff.position.set(0, 0, 0.6 + index * 0.002)
            puff.userData = {
                driftX: Math.cos(angle) * radius * (0.55 + Math.random() * 0.35),
                driftY: Math.sin(angle) * radius * (0.4 + Math.random() * 0.32),
                grow: 1.7 + Math.random() * 0.8,
                baseOpacity: puff.material.opacity
            }
            group.add(puff)
        }

        this.effectGroup.add(group)

        const animate = (now) => {
            const progress = clamp((now - startedAt) / duration, 0, 1)
            const eased = easeOutCubic(progress)
            ring.scale.setScalar(0.25 + eased * 1.55)
            shockwave.scale.setScalar(0.2 + eased * 1.3)
            flash.scale.setScalar(Math.max(0.02, 1 - progress * 0.55))
            ring.material.opacity = 0.72 * (1 - progress)
            shockwave.material.opacity = 0.36 * (1 - progress)
            flash.material.opacity = 0.62 * (1 - progress) * (1 - progress)

            for (const puff of group.children.slice(3)) {
                puff.position.x = puff.userData.driftX * eased
                puff.position.y = puff.userData.driftY * eased
                const scale = 1 + puff.userData.grow * eased
                puff.scale.set(scale, scale, scale)
                puff.material.opacity = puff.userData.baseOpacity * (1 - progress)
            }

            if (progress < 1 && group.parent) {
                group.userData.frame = requestAnimationFrame(animate)
                return
            }

            if (group.parent) {
                this.effectGroup.remove(group)
            }
            disposeObject(group)
        }

        group.userData.frame = requestAnimationFrame(animate)
    }

    addImpactCloud(x, y, options = {}) {
        const intensity = clamp(options.intensity ?? 1, 0.35, 2.2)
        const materialColor = getImpactCloudColor(options.material)
        const group = new THREE.Group()
        const puffCount = Math.round(4 + intensity * 3)
        const startedAt = performance.now()
        const duration = 420 + intensity * 120

        for (let index = 0; index < puffCount; index += 1) {
            const angle = (Math.PI * 2 * index) / puffCount + Math.random() * 0.55
            const distance = 0.05 + Math.random() * 0.14 * intensity
            const radius = 0.08 + Math.random() * 0.12 * intensity
            const puff = new THREE.Mesh(
                new THREE.CircleGeometry(radius, 18),
                new THREE.MeshBasicMaterial({
                    color: materialColor,
                    transparent: true,
                    opacity: 0.36 + Math.random() * 0.2,
                    depthWrite: false,
                    side: THREE.DoubleSide
                })
            )
            puff.position.set(
                Math.cos(angle) * distance,
                Math.sin(angle) * distance,
                PROJECTILE_Z + 0.08 + index * 0.002
            )
            puff.userData = {
                baseOpacity: puff.material.opacity,
                driftX: Math.cos(angle) * (0.15 + Math.random() * 0.2) * intensity,
                driftY: Math.sin(angle) * (0.11 + Math.random() * 0.18) * intensity + 0.06,
                grow: 1.45 + Math.random() * 0.9
            }
            group.add(puff)
        }

        group.position.set(x, y, 0)
        group.renderOrder = 28
        this.effectGroup.add(group)

        const animate = (now) => {
            const progress = clamp((now - startedAt) / duration, 0, 1)
            const eased = easeOutCubic(progress)

            for (const puff of group.children) {
                puff.position.x = puff.userData.driftX * eased
                puff.position.y = puff.userData.driftY * eased
                const scale = 1 + puff.userData.grow * eased
                puff.scale.set(scale, scale, scale)
                puff.material.opacity = puff.userData.baseOpacity * (1 - progress)
            }

            if (progress < 1 && group.parent) {
                group.userData.frame = requestAnimationFrame(animate)
                return
            }

            if (group.parent) {
                this.effectGroup.remove(group)
            }
            disposeObject(group)
        }

        group.userData.frame = requestAnimationFrame(animate)
    }

    addBlockBreakBurst(x, y, options = {}) {
        const material = options.material || 'wood'
        const width = Math.max(WORLD.grid * 0.5, options.width || BLOCK_SIZE.width)
        const height = Math.max(WORLD.grid * 0.5, options.height || BLOCK_SIZE.height)
        const area = clamp((width * height) / (WORLD.grid * WORLD.grid), 0.35, 8)
        const count = Math.round((material === 'glass' ? 12 : 9) + area * 2.2)
        const baseColor = getFragmentColor(material)
        const group = new THREE.Group()
        const startedAt = performance.now()
        const duration = material === 'glass' ? 820 : 720

        group.position.set(x, y, PROJECTILE_Z + 0.04)
        group.rotation.z = options.angle || 0
        group.renderOrder = 31

        for (let index = 0; index < count; index += 1) {
            const shardWidth = clamp(width * (0.08 + Math.random() * 0.13), 0.055, 0.34)
            const shardHeight = clamp(height * (0.08 + Math.random() * 0.16), 0.045, 0.28)
            const shard = new THREE.Mesh(
                new THREE.BoxGeometry(shardWidth, shardHeight, 0.045),
                new THREE.MeshBasicMaterial({
                    color: varyColor(baseColor, 0.18),
                    transparent: true,
                    opacity: 0.9,
                    depthWrite: false
                })
            )
            const localX = (Math.random() - 0.5) * width * 0.76
            const localY = (Math.random() - 0.5) * height * 0.76
            const outward = Math.atan2(localY || Math.random() - 0.5, localX || Math.random() - 0.5)
            const speed = 0.8 + Math.random() * (material === 'glass' ? 1.65 : 1.25)

            shard.position.set(localX, localY, 0.01 + index * 0.001)
            shard.rotation.z = Math.random() * Math.PI
            shard.userData = {
                vx: Math.cos(outward) * speed + (Math.random() - 0.5) * 0.4,
                vy: Math.sin(outward) * speed + 0.28 + Math.random() * 0.45,
                spin: (Math.random() - 0.5) * 9,
                scale: 0.72 + Math.random() * 0.55
            }
            shard.scale.setScalar(shard.userData.scale)
            group.add(shard)
        }

        this.effectGroup.add(group)
        this.addImpactCloud(x, y, {
            material,
            intensity: clamp(0.75 + area * 0.12, 0.8, 1.8)
        })

        const animate = (now) => {
            const progress = clamp((now - startedAt) / duration, 0, 1)
            const seconds = (now - startedAt) / 1000
            const eased = easeOutCubic(progress)

            for (const shard of group.children) {
                shard.position.x += shard.userData.vx * 0.018
                shard.position.y += (shard.userData.vy - seconds * 3.5) * 0.018
                shard.rotation.z += shard.userData.spin * 0.018
                shard.material.opacity = 0.9 * (1 - eased)
                const scale = shard.userData.scale * (1 - progress * 0.35)
                shard.scale.setScalar(Math.max(0.05, scale))
            }

            if (progress < 1 && group.parent) {
                group.userData.frame = requestAnimationFrame(animate)
                return
            }

            if (group.parent) {
                this.effectGroup.remove(group)
            }
            disposeObject(group)
        }

        group.userData.frame = requestAnimationFrame(animate)
    }

    addScorePopup(x, y, points) {
        const value = Math.round(Number(points) || 0)

        if (value <= 0) {
            return
        }

        const canvas = document.createElement('canvas')
        canvas.width = 192
        canvas.height = 96
        const context = canvas.getContext('2d')
        context.font = '900 38px Inter, Arial, sans-serif'
        context.textAlign = 'center'
        context.textBaseline = 'middle'
        context.lineWidth = 8
        context.strokeStyle = 'rgba(33, 20, 8, 0.88)'
        context.strokeText(`+${value}`, 96, 48)
        context.fillStyle = '#fff4cf'
        context.fillText(`+${value}`, 96, 48)

        const texture = new THREE.CanvasTexture(canvas)
        texture.colorSpace = THREE.SRGBColorSpace
        texture.needsUpdate = true

        const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: 1,
            depthWrite: false
        }))
        const startedAt = performance.now()
        const duration = 780
        sprite.position.set(x, y + 0.36, PROJECTILE_Z + 0.2)
        sprite.scale.set(1.25, 0.62, 1)
        sprite.renderOrder = 34
        this.effectGroup.add(sprite)

        const animate = (now) => {
            const progress = clamp((now - startedAt) / duration, 0, 1)
            const eased = easeOutCubic(progress)
            const scale = 1 + progress * 0.16
            sprite.position.y = y + 0.36 + eased * 0.82
            sprite.material.opacity = 1 - progress
            sprite.scale.set(1.25 * scale, 0.62 * scale, 1)

            if (progress < 1 && sprite.parent) {
                sprite.userData.frame = requestAnimationFrame(animate)
                return
            }

            if (sprite.parent) {
                this.effectGroup.remove(sprite)
            }
            disposeObject(sprite)
        }

        sprite.userData.frame = requestAnimationFrame(animate)
    }

    removeMesh(id) {
        const mesh = this.meshById.get(id)
        if (!mesh) {
            return
        }

        cancelMeshLifecycle(mesh)
        this.worldGroup.remove(mesh)
        disposeObject(mesh)
        this.meshById.delete(id)
    }

    syncBody(id, body) {
        const mesh = this.meshById.get(id)
        if (!mesh) {
            return
        }

        mesh.position.x = body.position.x
        mesh.position.y = body.position.y
        mesh.rotation.z = body.angle
    }

    setMeshTransform(id, x, y, rotation = null) {
        const mesh = this.meshById.get(id)
        if (!mesh) {
            return
        }

        mesh.position.x = x
        mesh.position.y = y

        if (rotation !== null) {
            mesh.rotation.z = rotation
        }
    }

    createLighting() {
        this.hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x56748a, 2.1)
        this.keyLight = new THREE.DirectionalLight(0xffffff, 1.6)
        this.keyLight.position.set(-5, 7, 10)
        this.scene.add(this.hemisphereLight, this.keyLight)
    }

    createBackdrop() {
        const skyLayer = new THREE.Group()
        this.skyGradient = makeSkyGradient()
        this.skyPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            this.skyGradient.material
        )
        this.skyPlane.position.set(0, 0, -8.5)
        this.skyPlane.renderOrder = SKY_RENDER_ORDER
        skyLayer.add(this.skyPlane)
        this.root.add(skyLayer)
        this.registerParallaxLayer(skyLayer, 1, 1)

        this.celestialLayer = new THREE.Group()
        this.sun = makeSun()
        this.moon = makeMoon()
        setBackgroundRenderOrder(this.sun, CELESTIAL_RENDER_ORDER)
        setBackgroundRenderOrder(this.moon, CELESTIAL_RENDER_ORDER)
        this.celestialLayer.add(this.sun, this.moon)
        this.root.add(this.celestialLayer)
        this.registerParallaxLayer(this.celestialLayer, 0.94, 0.82)

        this.hillMaterials = null
        this.createModeBackdrops()

        const ground = new THREE.Mesh(
            new THREE.BoxGeometry(BACKDROP_SPAN, 1.4, 0.6),
            new THREE.MeshStandardMaterial({ color: 0x5f8f55, roughness: 0.86 })
        )
        ground.position.set(0, WORLD.groundY - 0.72, -0.2)
        ground.visible = false

        const dirt = new THREE.Mesh(
            new THREE.BoxGeometry(BACKDROP_SPAN, 1.0, 0.62),
            new THREE.MeshStandardMaterial({ color: 0x6b4c35, roughness: 0.9 })
        )
        dirt.position.set(0, WORLD.groundY - 1.45, -0.18)
        dirt.visible = false

        this.nightToneLayer = new THREE.Group()
        this.nightTone = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            new THREE.MeshBasicMaterial({
                color: 0x071b42,
                transparent: true,
                opacity: 0,
                depthTest: false,
                depthWrite: false
            })
        )
        this.nightTone.position.set(0, 0, 8)
        this.nightTone.renderOrder = 100
        this.nightToneLayer.add(this.nightTone)
        this.scene.add(this.nightToneLayer)
        this.registerParallaxLayer(this.nightToneLayer, 1, 1)

        this.buildZone = makeBuildZone()
        this.stageSurface = makeStageSurface()
        this.setStageVisible(false)
        this.root.add(ground, dirt, this.stageSurface, this.buildZone)
        this.setBackdropMode('builder')
        this.layoutBackdrop()
        this.updateTimeEnvironment(true)
        this.updateBackdrop(0)
    }

    createModeBackdrops() {
        for (const [mode, layers] of Object.entries(MODE_BACKDROP_LAYERS)) {
            const group = new THREE.Group()
            group.visible = mode === this.activeBackdropMode
            this.backdropModeGroups[mode] = group
            this.root.add(group)

            for (const layer of layers) {
                const layerGroup = new THREE.Group()
                layerGroup.position.set(layer.x || 0, layer.y || 0, layer.z || 0)
                layerGroup.renderOrder = layer.renderOrder ?? 0

                const offsets = layer.repeat
                    ? [-layer.width, 0, layer.width]
                    : [0]
                const sourceMesh = makeTexturedPlane(this.textureLoader, layer)
                if (layer.sway) {
                    this.environment.addSway(sourceMesh.material, layer.sway)
                }

                offsets.forEach((offset, index) => {
                    const mesh = index === 0 ? sourceMesh : sourceMesh.clone()
                    mesh.position.x = offset
                    mesh.userData = {
                        baseX: offset,
                        speed: layer.speed || 0
                    }
                    layerGroup.add(mesh)
                })

                group.add(layerGroup)
                this.registerParallaxLayer(layerGroup, layer.parallaxX, layer.parallaxY)

                if (layer.speed) {
                    this.cloudLayers.push({
                        group: layerGroup,
                        span: layer.width * 3
                    })
                }
            }
        }
    }

    registerParallaxLayer(group, xFactor, yFactor = 0) {
        this.parallaxLayers.push({
            group,
            xFactor,
            yFactor,
            baseX: group.position.x,
            baseY: group.position.y
        })
    }

    layoutBackdrop() {
        const width = Math.max(BACKDROP_SPAN, this.viewWidth * 2.5)
        const height = Math.max(54, this.viewHeight * 2.25)

        if (this.skyPlane) {
            this.skyPlane.scale.set(width, height, 1)
        }

        if (this.nightTone) {
            this.nightTone.scale.set(width, height, 1)
        }

        if (this.lastClockState) {
            this.positionCelestialBody(this.lastClockState)
        }
    }

    updateBackdrop(delta = 0) {
        this.skyElapsed += delta
        this.updateTimeEnvironment()
        this.updateClouds()
        this.updateParallax()
    }

    updateClouds() {
        for (const layer of this.cloudLayers) {
            const minX = -layer.span / 2
            const maxX = layer.span / 2

            for (const cloud of layer.group.children) {
                cloud.position.x = wrapRange(
                    cloud.userData.baseX + this.environment.cloudTime * cloud.userData.speed,
                    minX,
                    maxX
                )
            }
        }
    }

    updateParallax() {
        for (const layer of this.parallaxLayers) {
            layer.group.position.x = layer.baseX + this.currentCamera.x * layer.xFactor
            layer.group.position.y = layer.baseY + this.currentCamera.y * layer.yFactor
        }
    }

    updateTimeEnvironment(force = false) {
        const nowMs = Date.now()

        if (!force && nowMs - this.lastSkyUpdateAt < SKY_STATE_REFRESH_MS) {
            return
        }

        this.lastSkyUpdateAt = nowMs
        const playerClockState = getPlayerClockState(new Date(nowMs), this.playerTimeZone)
        const clockState = getBackdropClockState(playerClockState, this.activeBackdropMode)
        const palette = getTimePalette(clockState.hour)
        this.lastClockState = clockState

        this.scene.background.copy(palette.skyTop)
        paintSkyGradient(this.skyGradient, palette)
        this.hemisphereLight.color.copy(palette.hemisphereSky)
        this.hemisphereLight.groundColor.copy(palette.hemisphereGround)
        this.hemisphereLight.intensity = palette.ambientIntensity
        this.keyLight.color.copy(palette.keyColor)
        this.keyLight.intensity = palette.keyIntensity

        for (const cloud of this.cloudMaterials) {
            cloud.material.color.copy(palette.cloudColor)
            cloud.material.opacity = palette.cloudOpacity * cloud.opacityScale
        }

        if (this.hillMaterials) {
            this.hillMaterials.far.color.copy(palette.farHill)
            this.hillMaterials.far.opacity = palette.farHillOpacity
            this.hillMaterials.mid.color.copy(palette.midHill)
            this.hillMaterials.mid.opacity = palette.midHillOpacity
            this.hillMaterials.near.color.copy(palette.nearHill)
            this.hillMaterials.near.opacity = palette.nearHillOpacity
        }

        this.nightTone.material.opacity = palette.overlayOpacity
        this.positionCelestialBody(clockState)
    }

    positionCelestialBody(clockState) {
        const activeBody = clockState.isDay ? this.sun : this.moon
        const inactiveBody = clockState.isDay ? this.moon : this.sun
        const progress = clockState.isDay ? clockState.sunProgress : clockState.nightProgress
        const arc = Math.sin(progress * Math.PI)
        const span = Math.max(WORLD.width, this.viewWidth)
        const x = lerp(-span * 0.38, span * 0.38, progress)
        const y = 1.6 + arc * 4.3

        activeBody.visible = true
        inactiveBody.visible = false
        activeBody.position.set(x, y, CELESTIAL_BACKDROP_Z)
        this.keyLight.position.set(x * 0.28, y, 10)
    }

    createLauncher() {
        this.launcher = new THREE.Group()
        this.slingCenterLocal = new THREE.Vector3(0, 1.82, 0.22)

        const shadow = new THREE.Mesh(
            new THREE.CircleGeometry(0.72, 36),
            new THREE.MeshBasicMaterial({ color: 0x111820, transparent: true, opacity: 0.18, depthWrite: false })
        )
        shadow.scale.y = 0.22
        shadow.position.set(0, -0.18, -0.18)

        const anchorHalo = new THREE.Mesh(
            new THREE.RingGeometry(0.42, 0.54, 42),
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.72,
                depthTest: false,
                side: THREE.DoubleSide
            })
        )
        anchorHalo.position.copy(this.slingCenterLocal)
        anchorHalo.renderOrder = 18

        const anchorCore = new THREE.Mesh(
            new THREE.CircleGeometry(0.18, 32),
            new THREE.MeshBasicMaterial({
                color: 0x141b22,
                transparent: true,
                opacity: 0.86,
                depthTest: false,
                side: THREE.DoubleSide
            })
        )
        anchorCore.position.copy(this.slingCenterLocal)
        anchorCore.position.z += 0.01
        anchorCore.renderOrder = 19

        this.slingGuide = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([this.slingCenterLocal, this.slingCenterLocal]),
            new THREE.LineBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
                depthWrite: false
            })
        )
        this.slingGuide.renderOrder = 22

        /*
        Legacy visual sling v1 kept for later deletion: this method previously
        built wood arms, fork tips, rubber bands, and a cup. The new destroy
        mechanic uses only this inert guide line so no sling geometry can be
        mistaken for a physics object.
        */
        this.launcher.add(shadow, this.slingGuide, anchorHalo, anchorCore)
        this.launcher.position.set(WORLD.launcherX, WORLD.launcherY, 0.24)
        this.root.add(this.launcher)
        this.clearSlingGuide()
    }

    createDestroyerRuler() {
        this.destroyerRuler = makeDestroyerRuler()
        this.destroyerRuler.visible = false
        this.root.add(this.destroyerRuler)
    }

    setBuildZoneVisible(visible) {
        this.buildZone.visible = visible
    }

    setBackdropMode(mode) {
        const nextMode = MODE_BACKDROP_LAYERS[mode] ? mode : 'builder'

        if (this.activeBackdropMode === nextMode) {
            return
        }

        this.activeBackdropMode = nextMode

        for (const [key, group] of Object.entries(this.backdropModeGroups)) {
            group.visible = key === nextMode
        }

        this.updateTimeEnvironment(true)
    }

    setStageMode(mode) {
        this.setBackdropMode(mode)
        const topY = mode === 'builder' ? WORLD.buildMinY : WORLD.groundY
        this.stageSurface.position.y = topY
        this.setStageVisible(true)
    }

    setStageVisible(visible) {
        if (this.stageSurface) {
            this.stageSurface.visible = visible
        }
    }

    setLauncherVisible(visible) {
        this.launcher.visible = visible
        if (!visible) {
            this.clearSlingGuide()
        }
    }

    setDestroyerRulerVisible(visible) {
        if (this.destroyerRuler) {
            this.destroyerRuler.visible = visible
        }
    }

    setSlingGuide(anchor, pullPoint, type = 'basic') {
        if (!this.slingGuide) {
            return
        }

        const localPull = new THREE.Vector3(
            pullPoint.x - this.launcher.position.x,
            pullPoint.y - this.launcher.position.y,
            0.22
        )
        const localAnchor = new THREE.Vector3(
            anchor.x - this.launcher.position.x,
            anchor.y - this.launcher.position.y,
            0.22
        )
        const config = BOMBS[type] || BOMBS.basic
        this.slingGuide.geometry.dispose()
        this.slingGuide.geometry = new THREE.BufferGeometry().setFromPoints([localAnchor, localPull])
        this.slingGuide.material.color.set(config.accent || 0xffffff)
        this.slingGuide.material.opacity = clamp(localPull.distanceTo(localAnchor) / 2.4, 0.22, 0.92)
        this.slingGuide.visible = localPull.distanceTo(localAnchor) > 0.04
    }

    clearSlingGuide() {
        if (!this.slingGuide || !this.slingCenterLocal) {
            return
        }

        this.slingGuide.geometry.dispose()
        this.slingGuide.geometry = new THREE.BufferGeometry().setFromPoints([this.slingCenterLocal, this.slingCenterLocal])
        this.slingGuide.visible = false
    }

    setLauncherTension(anchor, pullPoint) {
        this.setSlingGuide(anchor, pullPoint)
    }

    clearLauncherTension() {
        this.clearSlingGuide()
    }

}

function makeDestroyerRuler() {
    const group = new THREE.Group()
    const originX = WORLD.launcherX
    const y = WORLD.groundY - 0.48
    const z = 0.92
    const endValue = Math.ceil(WORLD.buildMaxX + 4 - originX)
    const points = [
        new THREE.Vector3(originX, y, z),
        new THREE.Vector3(originX + endValue, y, z)
    ]

    for (let value = 0; value <= endValue; value += 1) {
        const x = originX + value
        const major = value % 5 === 0
        const medium = value % 2 === 0
        const tickHeight = major ? 0.52 : medium ? 0.36 : 0.22
        points.push(
            new THREE.Vector3(x, y, z),
            new THREE.Vector3(x, y + tickHeight, z)
        )

        if (medium) {
            const label = makeRulerLabel(String(value), major)
            label.position.set(x, y - 0.28, z + 0.02)
            group.add(label)
        }
    }

    const line = new THREE.LineSegments(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.72,
            depthTest: false,
            depthWrite: false
        })
    )
    line.renderOrder = 28
    group.add(line)

    const buildStart = WORLD.buildMinX - originX
    const buildEnd = WORLD.buildMaxX - originX
    const buildWidth = buildEnd - buildStart
    const buildBand = new THREE.Mesh(
        new THREE.PlaneGeometry(buildWidth, 0.16),
        new THREE.MeshBasicMaterial({
            color: 0xffd95a,
            transparent: true,
            opacity: 0.34,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide
        })
    )
    buildBand.position.set(originX + buildStart + buildWidth / 2, y + 0.72, z - 0.01)
    buildBand.renderOrder = 27
    group.add(buildBand)

    return group
}

function makeRulerLabel(text, major = false) {
    const canvas = document.createElement('canvas')
    canvas.width = 128
    canvas.height = 64
    const context = canvas.getContext('2d')
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.font = `${major ? 800 : 700} ${major ? 34 : 28}px Inter, Arial, sans-serif`
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.lineWidth = major ? 8 : 7
    context.strokeStyle = 'rgba(10, 16, 22, 0.82)'
    context.strokeText(text, 64, 31)
    context.fillStyle = major ? '#ffe58a' : '#ffffff'
    context.fillText(text, 64, 31)

    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter

    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false
    }))
    sprite.scale.set(major ? 0.66 : 0.56, 0.33, 1)
    sprite.renderOrder = 29
    return sprite
}

function makeLauncherArm(wood, metal) {
    const group = new THREE.Group()
    const arm = new THREE.Mesh(
        new THREE.BoxGeometry(0.22, 1.55, 0.2),
        wood
    )
    arm.position.y = 0.38

    const cap = new THREE.Mesh(
        new THREE.CylinderGeometry(0.17, 0.17, 0.24, 18),
        wood
    )
    cap.rotation.x = Math.PI / 2
    cap.position.set(0, 1.2, 0.03)

    const band = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 0.16, 0.24),
        metal
    )
    band.position.set(0, 0.92, 0.04)

    const boltMaterial = new THREE.MeshStandardMaterial({ color: 0xc4a46b, roughness: 0.36, metalness: 0.45 })
    for (const x of [-0.08, 0.08]) {
        const bolt = new THREE.Mesh(
            new THREE.CircleGeometry(0.032, 12),
            boltMaterial
        )
        bolt.position.set(x, 0.92, 0.17)
        group.add(bolt)
    }

    group.add(arm, cap, band)
    return group
}

function makeLauncherCup(leather, metal) {
    const group = new THREE.Group()
    const cup = new THREE.Mesh(
        new THREE.SphereGeometry(0.28, 24, 12),
        leather
    )
    cup.scale.set(1.18, 0.7, 0.34)
    cup.position.z = 0.02

    const rim = new THREE.Mesh(
        new THREE.TorusGeometry(0.28, 0.025, 8, 28),
        metal
    )
    rim.scale.set(1.1, 0.68, 0.24)
    rim.position.z = 0.04

    group.add(cup, rim)
    return group
}

function updateLauncherBand(line, start, end) {
    if (!line) {
        return
    }

    line.geometry.dispose()
    line.geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(start.x, start.y, start.z),
        new THREE.Vector3(
            lerp(start.x, end.x, 0.46),
            lerp(start.y, end.y, 0.46) + 0.04,
            Math.max(start.z, end.z) + 0.02
        ),
        new THREE.Vector3(end.x, end.y, end.z)
    ])
    line.renderOrder = 5
}

function makeDistantCastle() {
    const group = new THREE.Group()
    const stone = new THREE.MeshBasicMaterial({ color: 0xd8d0bd, transparent: true, opacity: 0.72, depthWrite: false })
    const shade = new THREE.MeshBasicMaterial({ color: 0x9d9688, transparent: true, opacity: 0.64, depthWrite: false })
    const roof = new THREE.MeshBasicMaterial({ color: 0xbe4f37, transparent: true, opacity: 0.72, depthWrite: false })
    const flagBlue = new THREE.MeshBasicMaterial({ color: 0x1e7ed4, transparent: true, opacity: 0.78, depthWrite: false, side: THREE.DoubleSide })
    const flagRed = new THREE.MeshBasicMaterial({ color: 0xc74b35, transparent: true, opacity: 0.78, depthWrite: false, side: THREE.DoubleSide })

    const hill = new THREE.Mesh(
        new THREE.CircleGeometry(2.25, 36),
        new THREE.MeshBasicMaterial({ color: 0x577e50, transparent: true, opacity: 0.46, depthWrite: false })
    )
    hill.scale.y = 0.34
    hill.position.set(0, -0.52, -0.05)
    group.add(hill)

    const towers = [
        { x: -1.0, w: 0.42, h: 1.25, roof: 0.44, flag: flagRed },
        { x: -0.38, w: 0.5, h: 1.62, roof: 0.52, flag: flagBlue },
        { x: 0.34, w: 0.58, h: 1.9, roof: 0.58, flag: flagRed },
        { x: 1.08, w: 0.4, h: 1.2, roof: 0.4, flag: flagBlue }
    ]

    for (const tower of towers) {
        const body = new THREE.Mesh(new THREE.BoxGeometry(tower.w, tower.h, 0.08), stone)
        body.position.set(tower.x, tower.h / 2 - 0.2, 0)
        const side = new THREE.Mesh(new THREE.BoxGeometry(tower.w * 0.24, tower.h, 0.09), shade)
        side.position.set(tower.x - tower.w * 0.38, tower.h / 2 - 0.2, 0.01)
        const cap = makeTriangleMesh(tower.roof, tower.roof * 0.82, roof)
        cap.position.set(tower.x, tower.h - 0.06, 0.02)
        const flagPole = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.44, 0.02), shade)
        flagPole.position.set(tower.x, tower.h + 0.32, 0.03)
        const flag = makeFlagBanner(0.28, 0.16, tower.flag)
        flag.position.set(tower.x + 0.14, tower.h + 0.43, 0.04)
        group.add(body, side, cap, flagPole, flag)
    }

    const wall = new THREE.Mesh(new THREE.BoxGeometry(2.35, 0.7, 0.07), stone)
    wall.position.set(0.04, 0.05, -0.01)
    const wallShade = new THREE.Mesh(new THREE.BoxGeometry(2.35, 0.12, 0.08), shade)
    wallShade.position.set(0.04, 0.34, 0.01)
    const gate = new THREE.Mesh(
        new THREE.CircleGeometry(0.28, 18, 0, Math.PI),
        new THREE.MeshBasicMaterial({ color: 0x5f625f, transparent: true, opacity: 0.58, depthWrite: false, side: THREE.DoubleSide })
    )
    gate.rotation.z = Math.PI
    gate.scale.y = 1.3
    gate.position.set(0.12, -0.27, 0.04)

    group.add(wall, wallShade, gate)
    group.scale.setScalar(0.74)
    return group
}

function makeTriangleMesh(width, height, material) {
    const shape = new THREE.Shape()
    shape.moveTo(-width / 2, -height / 2)
    shape.lineTo(width / 2, -height / 2)
    shape.lineTo(0, height / 2)
    shape.lineTo(-width / 2, -height / 2)
    return new THREE.Mesh(new THREE.ShapeGeometry(shape), material)
}

function makeTrajectoryDot(point, index, color = new THREE.Color(0xffd95a)) {
    const group = new THREE.Group()
    const primary = index % 6 === 0
    const radius = primary ? 0.18 : 0.135
    const opacity = Math.max(0.54, 0.98 - index * 0.018)
    const halo = new THREE.Mesh(
        new THREE.CircleGeometry(radius * 1.45, 22),
        new THREE.MeshBasicMaterial({
            color: 0x1c120d,
            transparent: true,
            opacity: opacity * 0.28,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide
        })
    )
    const dot = new THREE.Mesh(
        new THREE.CircleGeometry(radius, 22),
        new THREE.MeshBasicMaterial({
            color: primary ? 0xffffff : color,
            transparent: true,
            opacity,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide
        })
    )

    group.add(halo, dot)
    halo.renderOrder = 24
    dot.renderOrder = 25
    group.position.set(point.x, point.y, AIM_PREVIEW_Z)
    group.renderOrder = 24
    return group
}

function makeTexturedPlane(loader, layer) {
    const texture = loadBackdropTexture(loader, layer.src)
    const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: layer.opacity ?? 1,
        depthWrite: false,
        side: THREE.DoubleSide
    })
    const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(layer.width, layer.height),
        material
    )

    mesh.renderOrder = layer.renderOrder ?? 0
    return mesh
}

function setBackgroundRenderOrder(group, renderOrder) {
    group.renderOrder = renderOrder
    group.traverse((node) => {
        node.renderOrder = renderOrder
        if (node.material) {
            node.material.depthTest = false
            node.material.depthWrite = false
        }
    })
}

function loadBackdropTexture(loader, src) {
    const texture = loader.load(`${BACKDROP_ART_PATH}${src}`, (loadedTexture) => {
        loadedTexture.colorSpace = THREE.SRGBColorSpace
        loadedTexture.minFilter = THREE.LinearFilter
        loadedTexture.magFilter = THREE.LinearFilter
        loadedTexture.generateMipmaps = false
        loadedTexture.needsUpdate = true
    })
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.generateMipmaps = false
    return texture
}

function makeSkyGradient() {
    const canvas = document.createElement('canvas')
    canvas.width = 4
    canvas.height = 256
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter

    return {
        canvas,
        texture,
        material: new THREE.MeshBasicMaterial({
            map: texture,
            depthWrite: false,
            depthTest: false
        })
    }
}

function paintSkyGradient(skyGradient, palette) {
    const context = skyGradient.canvas.getContext('2d')
    const gradient = context.createLinearGradient(0, 0, 0, skyGradient.canvas.height)
    gradient.addColorStop(0, colorToCss(palette.skyTop))
    gradient.addColorStop(0.64, colorToCss(palette.skyBottom))
    gradient.addColorStop(1, colorToCss(palette.horizon))
    context.fillStyle = gradient
    context.fillRect(0, 0, skyGradient.canvas.width, skyGradient.canvas.height)
    skyGradient.texture.needsUpdate = true
}

function makeSun() {
    const group = new THREE.Group()
    const glow = new THREE.Mesh(
        new THREE.CircleGeometry(0.74, 48),
        new THREE.MeshBasicMaterial({
            color: 0xffd56e,
            transparent: true,
            opacity: 0.16,
            depthWrite: false
        })
    )
    const disk = new THREE.Mesh(
        new THREE.CircleGeometry(0.28, 40),
        new THREE.MeshBasicMaterial({
            color: 0xfff1a5,
            transparent: true,
            opacity: 0.92,
            depthWrite: false
        })
    )

    group.add(glow, disk)
    return group
}

function makeMoon() {
    const group = new THREE.Group()
    const glow = new THREE.Mesh(
        new THREE.CircleGeometry(0.62, 48),
        new THREE.MeshBasicMaterial({
            color: 0xb8ccff,
            transparent: true,
            opacity: 0.13,
            depthWrite: false
        })
    )
    const disk = new THREE.Mesh(
        new THREE.CircleGeometry(0.25, 40),
        new THREE.MeshBasicMaterial({
            color: 0xeaf3ff,
            transparent: true,
            opacity: 0.86,
            depthWrite: false
        })
    )

    group.add(glow, disk)
    return group
}

function makeCloudMaterial(color, opacity) {
    return new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthWrite: false
    })
}

function makeCloudLayer(clouds, material) {
    const layer = new THREE.Group()

    for (const cloud of clouds) {
        const mesh = makeCloud(material)
        mesh.position.set(cloud.x, cloud.y, 0)
        mesh.scale.setScalar(cloud.scale)
        mesh.userData = {
            baseX: cloud.x,
            speed: cloud.speed
        }
        layer.add(mesh)
    }

    return layer
}

function makeCloud(material) {
    const group = new THREE.Group()
    const puffs = [
        { x: -0.78, y: -0.08, radius: 0.42, scaleX: 1.35, scaleY: 0.7 },
        { x: -0.34, y: 0.08, radius: 0.48, scaleX: 1.15, scaleY: 0.86 },
        { x: 0.2, y: 0.16, radius: 0.56, scaleX: 1.2, scaleY: 0.9 },
        { x: 0.74, y: -0.02, radius: 0.44, scaleX: 1.32, scaleY: 0.72 },
        { x: 0.02, y: -0.18, radius: 0.54, scaleX: 1.85, scaleY: 0.46 }
    ]

    for (const puff of puffs) {
        const mesh = new THREE.Mesh(
            new THREE.CircleGeometry(puff.radius, 22),
            material
        )
        mesh.position.set(puff.x, puff.y, 0)
        mesh.scale.set(puff.scaleX, puff.scaleY, 1)
        group.add(mesh)
    }

    return group
}

function makeHillBand(color, width, height, baseY, centerY, opacity) {
    const shape = new THREE.Shape()
    shape.moveTo(-width / 2, baseY)
    shape.bezierCurveTo(-width * 0.32, centerY + height * 0.2, -width * 0.22, centerY + height * 0.08, -width * 0.08, centerY + height * 0.55)
    shape.bezierCurveTo(width * 0.12, centerY + height * 0.08, width * 0.2, centerY + height * 0.45, width * 0.42, centerY + height * 0.12)
    shape.bezierCurveTo(width * 0.48, centerY + height * 0.05, width * 0.52, centerY, width / 2, baseY)
    shape.lineTo(-width / 2, baseY)

    const mesh = new THREE.Mesh(
        new THREE.ShapeGeometry(shape),
        new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity,
            depthWrite: false
        })
    )
    mesh.position.z = -3
    return mesh
}

function getViewportSize() {
    const doc = document.documentElement
    const visual = window.visualViewport
    const width = Math.max(
        1,
        Math.round(window.innerWidth || 0),
        Math.round(doc?.clientWidth || 0),
        Math.round(visual?.width || 0)
    )
    const height = Math.max(
        1,
        Math.round(window.innerHeight || 0),
        Math.round(doc?.clientHeight || 0),
        Math.round(visual?.height || 0)
    )

    return { width, height }
}

function makeBuildZone() {
    const width = WORLD.buildMaxX - WORLD.buildMinX
    const height = WORLD.buildMaxY - WORLD.buildMinY
    const group = new THREE.Group()
    const zone = new THREE.Mesh(
        new THREE.PlaneGeometry(width, height),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.08 })
    )
    zone.position.set(WORLD.buildMinX + width / 2, WORLD.buildMinY + height / 2, 0.05)

    const border = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.PlaneGeometry(width, height)),
        new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55 })
    )
    border.position.copy(zone.position)
    border.position.z = 0.08

    const floorRail = new THREE.Mesh(
        new THREE.BoxGeometry(width, 0.16, 0.04),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.48 })
    )
    floorRail.position.set(WORLD.buildMinX + width / 2, WORLD.buildMinY - 0.04, 0.16)

    const leftRail = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, height, 0.04),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.24 })
    )
    leftRail.position.set(WORLD.buildMinX, WORLD.buildMinY + height / 2, 0.15)

    const rightRail = leftRail.clone()
    rightRail.position.x = WORLD.buildMaxX

    const dots = []
    const majorDots = []
    const blockLaneDots = []

    for (let x = WORLD.buildMinX; x <= WORLD.buildMaxX + 0.001; x += WORLD.grid) {
        for (let y = WORLD.buildMinY; y <= WORLD.buildMaxY + 0.001; y += WORLD.grid) {
            const col = Math.round((x - WORLD.buildMinX) / WORLD.grid)
            const row = Math.round((y - WORLD.buildMinY) / WORLD.grid)
            const point = { x, y }

            if (col % 4 === 0 && row % 4 === 0) {
                majorDots.push(point)
            } else {
                dots.push(point)
            }
        }
    }

    for (let x = WORLD.buildMinX + BLOCK_SIZE.width / 2; x <= WORLD.buildMaxX - BLOCK_SIZE.width / 2 + 0.001; x += WORLD.grid) {
        for (let y = WORLD.buildMinY + BLOCK_SIZE.height / 2; y <= WORLD.buildMaxY - BLOCK_SIZE.height / 2 + 0.001; y += WORLD.grid) {
            blockLaneDots.push({ x, y })
        }
    }

    const dotMatrix = createDotMatrix(dots, 0.032, 0xffffff, 0.28)
    const majorDotMatrix = createDotMatrix(majorDots, 0.052, 0xffffff, 0.5)
    const blockLaneMatrix = createDotMatrix(blockLaneDots, 0.065, 0xfff2b3, 0.62)

    group.add(zone, dotMatrix, majorDotMatrix, blockLaneMatrix, floorRail, leftRail, rightRail, border)
    return group
}

function makeStageSurface() {
    const width = WORLD.buildMaxX - WORLD.buildMinX
    const centerX = WORLD.buildMinX + width / 2
    const group = new THREE.Group()

    const contact = new THREE.Mesh(
        new THREE.PlaneGeometry(width + 1.3, 0.42),
        new THREE.MeshBasicMaterial({
            color: 0x18212a,
            transparent: true,
            opacity: 0.18,
            depthWrite: false
        })
    )
    contact.position.set(centerX, 0.09, -0.34)

    const top = new THREE.Mesh(
        new THREE.BoxGeometry(width + 1.45, 0.14, 0.42),
        new THREE.MeshStandardMaterial({ color: 0x92784b, roughness: 0.82 })
    )
    top.position.set(centerX, -0.07, -0.24)

    const face = new THREE.Mesh(
        new THREE.BoxGeometry(width + 1.7, 0.46, 0.44),
        new THREE.MeshStandardMaterial({ color: 0x5a4633, roughness: 0.9 })
    )
    face.position.set(centerX, -0.37, -0.28)

    const lip = new THREE.Mesh(
        new THREE.BoxGeometry(width + 1.72, 0.07, 0.1),
        new THREE.MeshBasicMaterial({ color: 0xd9bd72 })
    )
    lip.position.set(centerX, 0.015, 0.02)

    const shadowLine = new THREE.Mesh(
        new THREE.BoxGeometry(width + 1.62, 0.05, 0.08),
        new THREE.MeshBasicMaterial({ color: 0x2a211a, transparent: true, opacity: 0.34 })
    )
    shadowLine.position.set(centerX, -0.16, 0.03)

    const leftEdge = makeStageEndCap()
    leftEdge.position.x = WORLD.buildMinX - 0.86
    const rightEdge = makeStageEndCap()
    rightEdge.position.x = WORLD.buildMaxX + 0.86

    group.add(contact, face, top, lip, shadowLine, leftEdge, rightEdge)
    return group
}

function makeStageEndCap() {
    const cap = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.5, 0.42),
        new THREE.MeshStandardMaterial({ color: 0x3f3127, roughness: 0.86 })
    )
    cap.position.y = -0.28
    cap.position.z = -0.2
    return cap
}

function makeFlagBanner(width, height, material) {
    return new THREE.Mesh(makeFlagBannerGeometry(width, height), material)
}

function makeFlagBannerOutline(width, height) {
    const left = -width / 2
    const right = width / 2
    const notchX = width * 0.18
    const halfHeight = height / 2
    const points = [
        new THREE.Vector3(left, halfHeight, 0),
        new THREE.Vector3(right, halfHeight, 0),
        new THREE.Vector3(notchX, 0, 0),
        new THREE.Vector3(right, -halfHeight, 0),
        new THREE.Vector3(left, -halfHeight, 0),
        new THREE.Vector3(left, halfHeight, 0)
    ]

    return new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({
            color: 0x8e2034,
            transparent: true,
            opacity: 0.72,
            depthTest: false
        })
    )
}

function makeFlagBannerGeometry(width, height) {
    const shape = new THREE.Shape()
    const left = -width / 2
    const right = width / 2
    const notchX = width * 0.18
    const halfHeight = height / 2

    shape.moveTo(left, halfHeight)
    shape.lineTo(right, halfHeight)
    shape.lineTo(notchX, 0)
    shape.lineTo(right, -halfHeight)
    shape.lineTo(left, -halfHeight)
    shape.lineTo(left, halfHeight)

    return new THREE.ShapeGeometry(shape)
}

function sampleLinearPoints(start, end, count) {
    const points = []
    const denominator = Math.max(1, count - 1)

    for (let index = 0; index < count; index += 1) {
        const progress = index / denominator
        points.push(new THREE.Vector3(
            lerp(start.x, end.x, progress),
            lerp(start.y, end.y, progress),
            lerp(start.z, end.z, progress)
        ))
    }

    return points
}

function getTrajectoryFade(progress) {
    if (progress <= TRAJECTORY_FADE_START_RATIO) {
        return 1
    }

    return 1 - clamp(
        (progress - TRAJECTORY_FADE_START_RATIO) / (TRAJECTORY_FADE_END_RATIO - TRAJECTORY_FADE_START_RATIO),
        0,
        1
    )
}

function createDotMatrix(points, radius, color, opacity) {
    const dot = new THREE.InstancedMesh(
        new THREE.CircleGeometry(radius, 12),
        new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity,
            depthWrite: false
        }),
        points.length
    )
    const matrix = new THREE.Matrix4()

    points.forEach((point, index) => {
        matrix.makeTranslation(point.x, point.y, 0.12)
        dot.setMatrixAt(index, matrix)
    })

    dot.instanceMatrix.needsUpdate = true
    return dot
}

function getPlayerTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null
    } catch (error) {
        return null
    }
}

function getPlayerClockState(date, timeZone) {
    const parts = getClockParts(date, timeZone)
    const hour = parts.hour + parts.minute / 60 + parts.second / 3600
    const isDay = hour >= DAY_START_HOUR && hour < DAY_END_HOUR
    const sunProgress = clamp((hour - DAY_START_HOUR) / (DAY_END_HOUR - DAY_START_HOUR), 0, 1)
    const nightProgress = hour >= DAY_END_HOUR
        ? (hour - DAY_END_HOUR) / (24 - DAY_END_HOUR + DAY_START_HOUR)
        : (hour + 24 - DAY_END_HOUR) / (24 - DAY_END_HOUR + DAY_START_HOUR)

    return {
        hour,
        isDay,
        sunProgress,
        nightProgress: clamp(nightProgress, 0, 1)
    }
}

function getBackdropClockState(clockState, mode) {
    const environment = MODE_ENVIRONMENT[mode]

    if (!environment) {
        return clockState
    }

    return {
        ...clockState,
        hour: environment.hour,
        isDay: environment.isDay,
        sunProgress: environment.sunProgress,
        nightProgress: environment.nightProgress
    }
}

function getClockParts(date, timeZone) {
    if (timeZone) {
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hourCycle: 'h23'
            }).formatToParts(date)
            const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))

            return {
                hour: Number(values.hour) || 0,
                minute: Number(values.minute) || 0,
                second: Number(values.second) || 0
            }
        } catch (error) {
            // Fall back to the browser-local clock if the reported time zone is unavailable.
        }
    }

    return {
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds()
    }
}

function getTimePalette(hour) {
    const normalizedHour = ((hour % 24) + 24) % 24

    for (let index = 1; index < SKY_PALETTE_STOPS.length; index += 1) {
        const previous = SKY_PALETTE_STOPS[index - 1]
        const next = SKY_PALETTE_STOPS[index]

        if (normalizedHour <= next.hour) {
            const amount = (normalizedHour - previous.hour) / (next.hour - previous.hour)
            return interpolatePalette(previous, next, clamp(amount, 0, 1))
        }
    }

    return interpolatePalette(SKY_PALETTE_STOPS[0], SKY_PALETTE_STOPS[1], 0)
}

function interpolatePalette(previous, next, amount) {
    const palette = {}

    for (const key of PALETTE_COLOR_KEYS) {
        palette[key] = new THREE.Color(previous[key]).lerp(new THREE.Color(next[key]), amount)
    }

    for (const key of PALETTE_NUMBER_KEYS) {
        palette[key] = lerp(previous[key], next[key], amount)
    }

    return palette
}

function colorToCss(color) {
    return `#${color.getHexString()}`
}

function lerp(start, end, amount) {
    return start + (end - start) * amount
}

function wrapRange(value, min, max) {
    const span = max - min
    return ((((value - min) % span) + span) % span) + min
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}

function easeOutCubic(value) {
    return 1 - (1 - value) ** 3
}

function getBlockDamageState(materialKey, healthRatio) {
    const ratio = clamp(Number.isFinite(healthRatio) ? healthRatio : 1, 0, 1)

    if (ratio <= 0) {
        return 'destroyed'
    }

    if (materialKey === 'stone') {
        if (ratio <= 0.4) {
            return 'heavy'
        }

        return ratio <= 0.8 ? 'light' : 'intact'
    }

    return ratio <= 0.5 ? 'heavy' : 'intact'
}

function getFlagDamageStage(ratio) {
    if (ratio <= 0) {
        return 0
    }

    if (ratio <= 0.2) {
        return 20
    }

    if (ratio <= 0.4) {
        return 40
    }

    if (ratio <= 0.6) {
        return 60
    }

    if (ratio <= 0.8) {
        return 80
    }

    return 100
}

function removeDamageOverlay(mesh) {
    const overlay = mesh.userData.damageOverlay

    if (!overlay) {
        return
    }

    mesh.remove(overlay)
    disposeObject(overlay)
    mesh.userData.damageOverlay = null
}

function removeFlagDamageOverlay(group) {
    for (const key of ['flagDamageOverlay', 'flagDamageTint']) {
        const overlay = group.userData[key]

        if (!overlay) {
            continue
        }

        group.remove(overlay)
        disposeObject(overlay)
        group.userData[key] = null
    }
}

function createFlagDamageOverlayMaterial(state) {
    const canvas = document.createElement('canvas')
    canvas.width = 256
    canvas.height = 160
    const context = canvas.getContext('2d')
    const severity = state === 'critical' ? 1 : state === 'heavy' ? 0.68 : 0.36

    context.clearRect(0, 0, canvas.width, canvas.height)
    context.save()
    context.lineCap = 'round'
    context.lineJoin = 'round'

    const tearCount = state === 'critical' ? 5 : state === 'heavy' ? 4 : 2
    for (let index = 0; index < tearCount; index += 1) {
        const x = canvas.width * (0.2 + Math.random() * 0.62)
        const y = canvas.height * (0.2 + Math.random() * 0.58)
        const length = canvas.width * (0.13 + severity * 0.15 + Math.random() * 0.08)
        const angle = -0.75 + Math.random() * 1.6
        context.strokeStyle = `rgba(70, 8, 14, ${0.55 + severity * 0.32})`
        context.lineWidth = 4 + severity * 5
        context.beginPath()
        context.moveTo(x, y)
        context.lineTo(x + Math.cos(angle) * length, y + Math.sin(angle) * length)
        context.stroke()
        context.strokeStyle = `rgba(255, 216, 188, ${0.28 + severity * 0.18})`
        context.lineWidth = 1.8 + severity * 2
        context.beginPath()
        context.moveTo(x - 2, y - 1)
        context.lineTo(x + Math.cos(angle) * length - 2, y + Math.sin(angle) * length - 1)
        context.stroke()
    }

    const scorchCount = state === 'critical' ? 9 : state === 'heavy' ? 6 : 3
    for (let index = 0; index < scorchCount; index += 1) {
        const radius = 6 + Math.random() * 16 * severity
        const x = canvas.width * (0.12 + Math.random() * 0.76)
        const y = canvas.height * (0.16 + Math.random() * 0.68)
        const gradient = context.createRadialGradient(x, y, 1, x, y, radius)
        gradient.addColorStop(0, `rgba(35, 8, 8, ${0.42 + severity * 0.26})`)
        gradient.addColorStop(1, 'rgba(35, 8, 8, 0)')
        context.fillStyle = gradient
        context.beginPath()
        context.arc(x, y, radius, 0, Math.PI * 2)
        context.fill()
    }

    if (state === 'critical') {
        context.fillStyle = 'rgba(0, 0, 0, 0.32)'
        context.beginPath()
        context.moveTo(canvas.width * 0.78, canvas.height * 0.08)
        context.lineTo(canvas.width * 0.98, canvas.height * 0.26)
        context.lineTo(canvas.width * 0.8, canvas.height * 0.38)
        context.closePath()
        context.fill()
    }

    context.restore()

    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.needsUpdate = true

    return new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 0.98,
        depthWrite: false,
        side: THREE.DoubleSide
    })
}

function fadeObjectOut(object, durationMs, onDone) {
    const startedAt = performance.now()

    const animate = (now) => {
        const progress = clamp((now - startedAt) / durationMs, 0, 1)
        setObjectOpacity(object, 1 - easeOutCubic(progress))

        if (progress < 1 && object.parent) {
            object.userData.fadeFrame = requestAnimationFrame(animate)
            return
        }

        object.userData.fadeFrame = null
        onDone?.()
    }

    object.userData.fadeFrame = requestAnimationFrame(animate)
}

function setObjectOpacity(object, opacity) {
    object.traverse((child) => {
        const materials = getObjectMaterials(child)

        for (const material of materials) {
            if (material.userData.fadeBaseOpacity === undefined) {
                material.userData.fadeBaseOpacity = material.opacity ?? 1
            }

            material.transparent = true
            material.depthWrite = false
            material.opacity = material.userData.fadeBaseOpacity * opacity
        }
    })
}

function cancelMeshLifecycle(object) {
    if (object.userData.destroyTimer) {
        clearTimeout(object.userData.destroyTimer)
        object.userData.destroyTimer = null
    }

    if (object.userData.fadeFrame) {
        cancelAnimationFrame(object.userData.fadeFrame)
        object.userData.fadeFrame = null
    }

    if (object.userData.frame) {
        cancelAnimationFrame(object.userData.frame)
        object.userData.frame = null
    }
}

function getImpactCloudColor(material) {
    if (material === 'glass') {
        return 0xd9fbff
    }

    if (material === 'wood') {
        return 0xd8a56d
    }

    if (material === 'flag') {
        return 0xffccd5
    }

    return 0xc7ccd2
}

function getFragmentColor(material) {
    if (material === 'glass') {
        return 0x8feeff
    }

    if (material === 'wood') {
        return 0xd99143
    }

    return 0xaeb5bd
}

function varyColor(color, amount = 0.12) {
    const threeColor = new THREE.Color(color)
    threeColor.offsetHSL(0, 0, (Math.random() - 0.5) * amount)
    return threeColor
}

function constrainCameraToBounds(target, bounds, viewWidth, viewHeight) {
    const halfWidth = viewWidth / target.zoom / 2
    const halfHeight = viewHeight / target.zoom / 2

    return {
        x: clampToRangeOrCenter(target.x, bounds.minX + halfWidth, bounds.maxX - halfWidth),
        y: clampToRangeOrCenter(target.y, bounds.minY + halfHeight, bounds.maxY - halfHeight),
        zoom: target.zoom
    }
}

function clampToRangeOrCenter(value, min, max) {
    if (min > max) {
        return (min + max) / 2
    }

    return clamp(value, min, max)
}

function drawImageCover(context, image, width, height) {
    const sourceWidth = image.width
    const sourceHeight = image.height

    if (!sourceWidth || !sourceHeight) {
        return
    }

    const scale = Math.max(width / sourceWidth, height / sourceHeight)
    const drawWidth = sourceWidth * scale
    const drawHeight = sourceHeight * scale
    const x = (width - drawWidth) / 2
    const y = (height - drawHeight) / 2

    context.drawImage(image, x, y, drawWidth, drawHeight)
}

function disposeObject(object) {
    object.traverse((child) => {
        if (child.geometry) {
            child.geometry.dispose()
        }

        for (const material of getObjectMaterials(child)) {
            disposeMaterial(material)
        }
    })
}

function getObjectMaterials(object) {
    if (!object.material) {
        return []
    }

    return Array.isArray(object.material) ? object.material : [object.material]
}

function disposeMaterial(material) {
    if (material.map && !material.map.userData?.cachedBlockTexture) {
        material.map.dispose()
    }

    material.dispose()
}
